import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useTRPC } from "@/lib/trpc";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { formatDistanceToNow } from "date-fns";
import { FileIcon, ImageIcon, Search, VideoIcon } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { MediaUploadModal } from "./media-upload-modal";

export interface MediaItem {
  id: string;
  title: string;
  description: string;
  createdAt: Date;
  cloudStorageObject: {
    id: string;
    fullUrl: string;
    mimeType?: string | null;
    fileExtension?: string | null;
    public?: boolean;
    uploadConfirmedAt?: Date | null;
  };
}

interface ImageSelectionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (imageUrl: string, alt?: string) => void;
  title?: string;
  acceptedMimeTypes?: string[];
}

export function ImageSelectionModal({
  open,
  onOpenChange,
  onSelect,
  title = "Select Image",
  acceptedMimeTypes,
}: ImageSelectionModalProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("browse");

  const queryClient = useQueryClient();
  const trpc = useTRPC();

  const {
    data: mediaList,
    isLoading,
    refetch,
  } = useQuery(trpc.media.list.queryOptions());

  // Filter by accepted mime types if specified
  const typeFilteredMedia = acceptedMimeTypes
    ? mediaList?.filter((media) =>
        acceptedMimeTypes.includes(media.cloudStorageObject.mimeType || ""),
      ) || []
    : mediaList || [];

  const filteredMedia = typeFilteredMedia.filter(
    (media) =>
      media.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      media.description.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const getMediaTypeIcon = (mimeType?: string | null) => {
    if (!mimeType) return <FileIcon className="h-4 w-4" />;
    if (mimeType.startsWith("image/")) return <ImageIcon className="h-4 w-4" />;
    if (mimeType.startsWith("video/")) return <VideoIcon className="h-4 w-4" />;
    return <FileIcon className="h-4 w-4" />;
  };

  const getMediaTypeLabel = (mimeType?: string | null) => {
    if (!mimeType) return "File";
    if (mimeType.startsWith("image/")) return "Image";
    if (mimeType.startsWith("video/")) return "Video";
    return "File";
  };

  const handleMediaSelect = (media: MediaItem) => {
    onSelect(media.cloudStorageObject.fullUrl, media.title);
    onOpenChange(false);
    toast.success(`Selected: ${media.title}`);
  };

  const handleUploadSuccess = (mediaId: string) => {
    setUploadModalOpen(false);
    setActiveTab("browse");
    queryClient.invalidateQueries(trpc.media.list.queryOptions());

    // Find and select the newly uploaded media
    setTimeout(() => {
      refetch().then((result) => {
        const newMedia = result.data?.find((m) => m.id === mediaId);
        if (newMedia) {
          handleMediaSelect(newMedia);
        }
      });
    }, 500);
  };

  const MediaGrid = () => {
    if (isLoading) {
      return (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <Card key={i} className="overflow-hidden">
              <div className="h-32 w-full bg-muted animate-pulse" />
              <CardContent className="pt-4">
                <div className="h-4 w-2/3 mb-2 bg-muted animate-pulse rounded" />
                <div className="h-3 w-full bg-muted animate-pulse rounded" />
              </CardContent>
            </Card>
          ))}
        </div>
      );
    }

    if (filteredMedia.length === 0) {
      return (
        <div className="text-center py-12">
          <ImageIcon className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">No media found</h3>
          <p className="text-muted-foreground mb-4">
            {searchTerm
              ? "Try adjusting your search terms"
              : "Upload some media to get started"}
          </p>
          <Button onClick={() => setActiveTab("upload")}>Upload Media</Button>
        </div>
      );
    }

    return (
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {filteredMedia.map((media) => (
          <Card
            key={media.id}
            className="overflow-hidden cursor-pointer transition-all hover:shadow-md"
            onClick={() => handleMediaSelect(media)}
          >
            <div className="relative h-32 bg-muted">
              {media.cloudStorageObject.mimeType?.startsWith("image/") ? (
                <img
                  src={media.cloudStorageObject.fullUrl}
                  alt={media.title}
                  className="h-full w-full object-cover"
                />
              ) : (
                <div className="flex items-center justify-center h-full w-full">
                  {getMediaTypeIcon(media.cloudStorageObject.mimeType)}
                </div>
              )}
              <Badge
                className="absolute top-2 right-2 flex items-center gap-1"
                variant="secondary"
              >
                {getMediaTypeIcon(media.cloudStorageObject.mimeType)}
                <span className="text-xs">
                  {getMediaTypeLabel(media.cloudStorageObject.mimeType)}
                </span>
              </Badge>
            </div>
            <CardContent className="pt-3 pb-2">
              <h4 className="font-medium text-sm line-clamp-1 mb-1">
                {media.title}
              </h4>
              <p className="text-xs text-muted-foreground line-clamp-2">
                {media.description}
              </p>
            </CardContent>
            <CardFooter className="text-xs text-muted-foreground pt-0 pb-3">
              {formatDistanceToNow(new Date(media.createdAt), {
                addSuffix: true,
              })}
            </CardFooter>
          </Card>
        ))}
      </div>
    );
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-6xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
          </DialogHeader>

          <Tabs
            value={activeTab}
            onValueChange={setActiveTab}
            className="h-full"
          >
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="browse">Browse Media</TabsTrigger>
              <TabsTrigger value="upload">Upload New</TabsTrigger>
            </TabsList>

            <TabsContent value="browse" className="space-y-4 mt-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search media..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>

              <div className="max-h-96 overflow-y-auto">
                <MediaGrid />
              </div>
            </TabsContent>

            <TabsContent value="upload" className="mt-4">
              <div className="text-center py-8">
                <Button onClick={() => setUploadModalOpen(true)} size="lg">
                  Upload New Media
                </Button>
                <p className="text-sm text-muted-foreground mt-4">
                  Upload a new file and it will be automatically selected
                </p>
              </div>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

      <MediaUploadModal
        open={uploadModalOpen}
        onOpenChange={setUploadModalOpen}
        onSuccess={handleUploadSuccess}
        acceptedFileTypes={acceptedMimeTypes
          ?.map((type) => `.${type.split("/")[1]}`)
          .join(",")}
        fileTypeLabel={
          acceptedMimeTypes
            ? `Accepted formats: ${acceptedMimeTypes.map((t) => t.split("/")[1].toUpperCase()).join(", ")}`
            : undefined
        }
      />
    </>
  );
}
